import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import supabase from '../config/supabase';
import config from '../config/environment';
import type { PortalRole } from '../types/portalUser';
import { getDefaultPathForRoles, hasRequiredRole, normalizePortalRoles } from '../utils/permissions';
import { onPortalSessionInvalidated, requestEdgeFunction } from '../services/edgeFunctionClient';
import {
  PORTAL_USER_INACTIVE_ERROR,
  markAccountDeactivatedLogout,
} from '../utils/portalSession';

interface AuthState {
  user: User | null;
  session: Session | null;
  roles: PortalRole[];
  loading: boolean;
}

interface AuthContextData extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  defaultPath: string;
  hasRole: (allowedRoles?: PortalRole[]) => boolean;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

type PortalSessionLoadResult =
  | { status: 'ok'; roles: PortalRole[] }
  | { status: 'inactive' }
  | { status: 'unauthenticated' }
  | { status: 'error' };

const loadCurrentPortalSession = async (): Promise<PortalSessionLoadResult> => {
  try {
    const response = await requestEdgeFunction<{
      success?: boolean;
      data?: { roles?: PortalRole[]; is_active?: boolean };
    }>('manage-portal-account', {
      method: 'GET',
    });

    if (response.data?.is_active === false) {
      return { status: 'inactive' };
    }

    return { status: 'ok', roles: normalizePortalRoles(response.data?.roles ?? []) };
  } catch (error) {
    const message = error instanceof Error ? error.message : '';
    if (message === PORTAL_USER_INACTIVE_ERROR) {
      return { status: 'inactive' };
    }

    const normalized = message.trim().toLowerCase();
    if (
      normalized === 'unauthorized'
      || normalized.includes('não autenticada')
    ) {
      return { status: 'unauthenticated' };
    }

    return { status: 'error' };
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    roles: [],
    loading: true,
  });

  const clearLocalSession = useCallback(() => {
    setState({
      user: null,
      session: null,
      roles: [],
      loading: false,
    });
  }, []);

  const signOutInvalidSession = useCallback(async (reason: 'inactive' | 'unauthenticated') => {
    if (reason === 'inactive') {
      markAccountDeactivatedLogout();
    }

    const { error } = await supabase.auth.signOut();
    if (error) {
      console.warn('[auth] Failed to sign out invalid session', error);
    }

    clearLocalSession();
  }, [clearLocalSession]);

  useEffect(() => {
    let cancelled = false;

    const syncSessionState = async (session: Session | null) => {
      if (!session?.user) {
        if (!cancelled) {
          clearLocalSession();
        }
        return;
      }

      const result = await loadCurrentPortalSession();

      if (cancelled) {
        return;
      }

      if (result.status === 'inactive') {
        await signOutInvalidSession('inactive');
        return;
      }

      if (result.status === 'unauthenticated') {
        await signOutInvalidSession('unauthenticated');
        return;
      }

      if (result.status === 'error') {
        setState({
          user: session.user,
          session,
          roles: [],
          loading: false,
        });
        return;
      }

      setState({
        user: session.user,
        session,
        roles: result.roles,
        loading: false,
      });
    };

    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        if (cancelled) {
          return;
        }
        void syncSessionState(session);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        const isAbort = error instanceof Error
          && (error.name === 'AbortError' || error.message.toLowerCase().includes('signal is aborted'));

        if (!isAbort) {
          console.warn('[auth] Failed to restore session', error);
        }

        setState((prev) => ({ ...prev, loading: false }));
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      void syncSessionState(session);
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      void supabase.auth.getSession().then(({ data: { session } }) => {
        if (!cancelled && session?.user) {
          void syncSessionState(session);
        }
      });
    };

    const handleWindowFocus = () => {
      void supabase.auth.getSession().then(({ data: { session } }) => {
        if (!cancelled && session?.user) {
          void syncSessionState(session);
        }
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    onPortalSessionInvalidated((reason) => {
      if (cancelled) {
        return;
      }

      void signOutInvalidSession(reason);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      onPortalSessionInvalidated(null);
    };
  }, [clearLocalSession, signOutInvalidSession]);

  const signIn = useCallback(async (email: string, password: string) => {
    const response = await fetch(`${config.supabaseFunctionsUrl}/authenticate-portal-user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result?.error ?? 'Erro ao fazer login. Tente novamente.');
    }

    const { error: sessionError } = await supabase.auth.setSession({
      access_token: result.session.access_token,
      refresh_token: result.session.refresh_token,
    });

    if (sessionError) throw sessionError;
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, []);

  const hasRole = useCallback(
    (allowedRoles?: PortalRole[]) => hasRequiredRole(state.roles, allowedRoles),
    [state.roles],
  );
  const defaultPath = getDefaultPathForRoles(state.roles);

  return (
    <AuthContext.Provider value={{ ...state, signIn, signOut, hasRole, defaultPath }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
